import dayjs, { type Dayjs } from 'dayjs';
import { type ReactElement, useCallback, useMemo, useState } from 'react';
import {
  buildTestCounts,
  type TestStatusKey,
} from '@/features/dashboard/components/dashboardStatusDataHelpers';
import { formatRangeWindowNoDataText } from '@/features/dashboard/hooks';
import { queryTestExecutions, queryTestExecutionStatusSummary } from '@/features/executions/data';
import { EntityTrendsPageShell } from '@/features/shared/components/EntityTrendsPageShell';
import { CLONE_TABLE_PANEL_HEADER_HEIGHT } from '@/features/shared/components/tableSystem/tableConstants';
import { TableToolbar } from '@/features/shared/components/tableSystem/TableToolbar';
import {
  resolveMetaDateBounds,
  type TableQueryMeta,
} from '@/features/shared/components/tableSystem/tableToolbar.helpers';
import { useCloneDateWindow } from '@/features/shared/hooks/useCloneDateWindow';
import { useEntityTrendsPage } from '@/features/shared/hooks/useEntityTrendsPage';
import { useSuiteOptions } from '@/features/suites';
import { TestExecutionsTable } from '@/features/tests/components/TestExecutionsTable';
import { TestsTable } from '@/features/tests/components/TestsTable';
import { queryTests, queryTestStatusSummary } from '@/features/tests/data/tests';
import {
  cloneExecutionDefaultColumnVisibilityModel,
  cloneExecutionDefaultSortDirection,
  cloneExecutionDefaultSortKey,
  cloneExecutionSortFieldMap,
  cloneExecutionSortOptions,
  cloneExecutionStatusOptions,
  cloneExecutionColumnOptions,
  type CloneExecutionSortDirection,
  type CloneExecutionSortKey,
} from '@/features/tests/pages/testExecutionsTableDefinition';
import {
  type CloneSortDirection,
  type CloneSortKey,
  testsTableDefinition,
} from '@/features/tests/pages/testsTableDefinition';
import { testExecutionSummaryStatusOptions } from '@/shared/constants/statusOptions';
import { gridPaths, queryKeys, useResolvedFeatureFlags, useTagOptions } from '@/shared/hooks';
import { Stack } from '@/ui';
import {
  buildDateRangeFilters,
  buildEqFilterValue,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import { Panel } from '@/ui/composites/Panel';
import { Box } from '@/ui/primitives';
import { Tab, Tabs, type GridColumnVisibilityModel } from '@/ui/primitives';
import type { CloneDateQuickFilter } from '@/features/shared/hooks/useCloneDateWindow';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type {
  QueryResult,
  TestExecutionDto,
  TestExecutionStatus,
  TestExecutionStatusSummaryDto,
  TestListItemDto,
  TestSummaryDto,
} from '@flowqa/shared';

type CloneTestsPanelTab = 'tests' | 'executions';

export const TestsPage = (): ReactElement => {
  const { flags: featureFlags } = useResolvedFeatureFlags();
  const cloneTableEnabled = featureFlags?.testsCloneTable ?? false;
  const viewModel = useEntityTrendsPage<
    TestListItemDto,
    TestSummaryDto | TestExecutionStatusSummaryDto
  >({
    loadingMessage: 'Loading tests...',
    metricsScope: 'test',
    gridPath: gridPaths.tests,
    executionGridPath: gridPaths.testExecutions,
    entitySearchField: 'name',
    executionSearchField: 'testName',
    statusOptions: testExecutionSummaryStatusOptions.map((option) => ({
      value: option.value as Exclude<TestStatusKey, 'untested'>,
      label: option.label,
    })),
    summaryKey: (projectId, organisationId, filtersKey) =>
      queryKeys.testSummary(projectId, organisationId, filtersKey),
    executionSummaryKey: (projectId, organisationId, filtersKey) =>
      queryKeys.testExecutionSummary(projectId, organisationId, filtersKey),
    summaryPrefixKey: queryKeys.testSummaryPrefix,
    executionSummaryPrefixKey: queryKeys.testExecutionSummaryPrefix,
    queryLatestSummary: queryTestStatusSummary,
    queryExecutionSummary: queryTestExecutionStatusSummary,
    buildCounts: buildTestCounts,
    selectThresholds: (thresholds) => thresholds.tests,
    queryEntityTable: async (
      input: GridQueryInput,
      {
        selectedProjectId,
        selectedOrganisationId,
        rangeStartIso,
        rangeEndIso,
        previousRangeStartIso,
        previousRangeEndIso,
        deltasEnabled,
      },
    ): Promise<QueryResult<TestListItemDto>> =>
      queryTests(selectedProjectId, selectedOrganisationId ?? undefined, {
        ...input,
        metricsStartDay: rangeStartIso ?? undefined,
        metricsEndDay: rangeEndIso ?? undefined,
        previousMetricsStartDay: previousRangeStartIso ?? undefined,
        previousMetricsEndDay: previousRangeEndIso ?? undefined,
        include: {
          suites: { ids: true, items: true, primary: true },
          latestExecution: true,
          executionStatusCounts: true,
          metrics: true,
          metricDeltas: deltasEnabled && previousRangeStartIso !== null,
        },
      }),
  });
  const { tagOptions } = useTagOptions({ projectId: viewModel.selectedProjectId });
  const { suiteOptions } = useSuiteOptions({ projectId: viewModel.selectedProjectId });
  const initialCloneColumnVisibilityModel: GridColumnVisibilityModel = {
    ...testsTableDefinition.defaultColumnVisibilityModel,
  };
  const initialCloneExecutionColumnVisibilityModel: GridColumnVisibilityModel = {
    ...cloneExecutionDefaultColumnVisibilityModel,
  };

  const [cloneDateQuickFilter, setCloneDateQuickFilter] = useState<CloneDateQuickFilter>('7d');
  const [cloneFromDate, setCloneFromDate] = useState<Dayjs | null>(null);
  const [cloneToDate, setCloneToDate] = useState<Dayjs | null>(null);
  const [debouncedCloneSearch, setDebouncedCloneSearch] = useState('');
  const [cloneColumnVisibilityModel, setCloneColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(initialCloneColumnVisibilityModel);
  const [cloneSortKey, setCloneSortKey] = useState<CloneSortKey>(
    testsTableDefinition.defaultSortKey,
  );
  const [cloneSortDirection, setCloneSortDirection] = useState<CloneSortDirection>(
    testsTableDefinition.defaultSortDirection,
  );
  const [cloneFilterStatuses, setCloneFilterStatuses] = useState<TestExecutionStatus[]>([]);
  const [cloneFilterSuites, setCloneFilterSuites] = useState<string[]>([]);
  const [cloneFilterTags, setCloneFilterTags] = useState<string[]>([]);
  const [cloneActiveTab, setCloneActiveTab] = useState<CloneTestsPanelTab>('tests');
  const [debouncedCloneExecutionSearch, setDebouncedCloneExecutionSearch] = useState('');
  const [cloneExecutionColumnVisibilityModel, setCloneExecutionColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(initialCloneExecutionColumnVisibilityModel);
  const [cloneExecutionSortKey, setCloneExecutionSortKey] = useState<CloneExecutionSortKey>(
    cloneExecutionDefaultSortKey,
  );
  const [cloneExecutionSortDirection, setCloneExecutionSortDirection] =
    useState<CloneExecutionSortDirection>(cloneExecutionDefaultSortDirection);
  const [cloneExecutionFilterStatuses, setCloneExecutionFilterStatuses] = useState<
    TestExecutionStatus[]
  >([]);
  const [cloneExecutionFilterSuites, setCloneExecutionFilterSuites] = useState<string[]>([]);
  const [cloneExecutionFilterTags, setCloneExecutionFilterTags] = useState<string[]>([]);
  const [cloneTestsMeta, setCloneTestsMeta] = useState<TableQueryMeta<TestListItemDto> | null>(
    null,
  );
  const [cloneTestExecutionsMeta, setCloneTestExecutionsMeta] =
    useState<TableQueryMeta<TestExecutionDto> | null>(null);

  const cloneAvailableTags = useMemo(
    () =>
      Array.from(new Set(tagOptions.map((option) => option.value))).sort((left, right) =>
        left.localeCompare(right),
      ),
    [tagOptions],
  );
  const cloneExecutionAvailableTags = useMemo(
    () =>
      Array.from(new Set(tagOptions.map((option) => option.value))).sort((left, right) =>
        left.localeCompare(right),
      ),
    [tagOptions],
  );
  const handleDebouncedCloneSearchChange = useCallback((value: string) => {
    setDebouncedCloneSearch(value);
  }, []);
  const handleDebouncedCloneExecutionSearchChange = useCallback((value: string) => {
    setDebouncedCloneExecutionSearch(value);
  }, []);
  const showCloneDeltas = viewModel.deltasEnabled;
  const cloneDateWindow = useCloneDateWindow({
    dateQuickFilter: cloneDateQuickFilter,
    fromDate: cloneFromDate,
    toDate: cloneToDate,
  });
  const cloneQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<TestListItemDto>> => {
      const mappedFilters = input.filters.map((filter) =>
        filter.field === 'statusFilter'
          ? {
              ...filter,
              field: 'latestExecutionStatus',
            }
          : filter,
      );
      const toolbarFilters = mergeFilters(
        buildInFilter('latestExecutionStatus', cloneFilterStatuses),
        buildInFilter('suiteId', cloneFilterSuites),
        buildInFilter('tags', cloneFilterTags),
      );
      const sortOverride: ApiSort = {
        field: testsTableDefinition.sortFieldMap[cloneSortKey],
        direction: cloneSortDirection,
      };
      return queryTests(
        viewModel.selectedProjectId,
        viewModel.selectedOrganisationId ?? undefined,
        {
          ...input,
          sorts: [sortOverride],
          filters: [...mappedFilters, ...toolbarFilters],
          metricsStartDay: cloneDateWindow.startDay ?? undefined,
          metricsEndDay: cloneDateWindow.endDay ?? undefined,
          previousMetricsStartDay: cloneDateWindow.previousStartDay ?? undefined,
          previousMetricsEndDay: cloneDateWindow.previousEndDay ?? undefined,
          include: {
            suites: { ids: true, items: true, primary: true },
            latestExecution: true,
            executionStatusCounts: true,
            metrics: true,
            metricDeltas: showCloneDeltas,
          },
        },
      );
    },
    [
      cloneDateWindow.endDay,
      cloneDateWindow.previousEndDay,
      cloneDateWindow.previousStartDay,
      cloneDateWindow.startDay,
      cloneFilterStatuses,
      cloneFilterSuites,
      cloneFilterTags,
      cloneSortDirection,
      cloneSortKey,
      showCloneDeltas,
      viewModel.selectedOrganisationId,
      viewModel.selectedProjectId,
    ],
  );
  const cloneExecutionQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<TestExecutionDto>> => {
      const isLatestQuickFilter =
        cloneDateQuickFilter === 'latest' && cloneFromDate === null && cloneToDate === null;
      const baseFilters = isLatestQuickFilter
        ? input.filters.filter(
            (filter) =>
              filter.field !== 'timestamp' &&
              filter.field !== 'createdAt' &&
              filter.field !== 'isLatest',
          )
        : input.filters;
      const toolbarFilters = mergeFilters(
        buildInFilter('status', cloneExecutionFilterStatuses),
        buildInFilter('suiteId', cloneExecutionFilterSuites),
        buildInFilter('tags', cloneExecutionFilterTags),
        isLatestQuickFilter
          ? buildEqFilterValue('isLatest', true)
          : buildDateRangeFilters(
              'timestamp',
              cloneDateWindow.startDay
                ? dayjs(cloneDateWindow.startDay).startOf('day').toISOString()
                : null,
              cloneDateWindow.endDay
                ? dayjs(cloneDateWindow.endDay).endOf('day').toISOString()
                : null,
            ),
      );
      const sortOverride: ApiSort = {
        field: cloneExecutionSortFieldMap[cloneExecutionSortKey],
        direction: cloneExecutionSortDirection,
      };
      return queryTestExecutions(
        viewModel.selectedProjectId,
        viewModel.selectedOrganisationId ?? undefined,
        {
          ...input,
          search: debouncedCloneExecutionSearch,
          sorts: [sortOverride],
          filters: [...baseFilters, ...toolbarFilters],
        },
      );
    },
    [
      cloneDateWindow.endDay,
      cloneDateWindow.startDay,
      cloneDateQuickFilter,
      cloneFromDate,
      cloneToDate,
      cloneExecutionFilterStatuses,
      cloneExecutionFilterSuites,
      cloneExecutionFilterTags,
      cloneExecutionSortDirection,
      cloneExecutionSortKey,
      debouncedCloneExecutionSearch,
      viewModel.selectedOrganisationId,
      viewModel.selectedProjectId,
    ],
  );
  const cloneNoRowsText = cloneDateWindow.hasCustomBounds
    ? 'No tests found for selected date range'
    : formatRangeWindowNoDataText('No tests found', cloneDateQuickFilter, false);
  const cloneExecutionsNoRowsText = cloneDateWindow.hasCustomBounds
    ? 'No test executions found for selected date range'
    : formatRangeWindowNoDataText('No test executions found', cloneDateQuickFilter, false);
  const cloneStatusFilterKey = useMemo(
    () => [...cloneFilterStatuses].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneFilterStatuses],
  );
  const cloneSuiteFilterKey = useMemo(
    () => [...cloneFilterSuites].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneFilterSuites],
  );
  const cloneTagFilterKey = useMemo(
    () => [...cloneFilterTags].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneFilterTags],
  );
  const cloneExecutionStatusFilterKey = useMemo(
    () =>
      [...cloneExecutionFilterStatuses].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneExecutionFilterStatuses],
  );
  const cloneExecutionSuiteFilterKey = useMemo(
    () =>
      [...cloneExecutionFilterSuites].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneExecutionFilterSuites],
  );
  const cloneExecutionTagFilterKey = useMemo(
    () => [...cloneExecutionFilterTags].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneExecutionFilterTags],
  );
  const cloneTestExecutionDateBounds = useMemo(
    () => resolveMetaDateBounds(cloneTestExecutionsMeta, (item) => item.timestamp),
    [cloneTestExecutionsMeta],
  );
  const cloneContentOverride = cloneTableEnabled ? (
    <Panel
      padding="xl"
      gap="none"
      contentGap="md"
      headerGap="none"
      headerSlot={
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: `${CLONE_TABLE_PANEL_HEADER_HEIGHT}px`,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap="md"
            sx={{
              minWidth: 0,
              minHeight: `${CLONE_TABLE_PANEL_HEADER_HEIGHT}px`,
              height: `${CLONE_TABLE_PANEL_HEADER_HEIGHT}px`,
            }}
          >
            <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
              <Tabs
                value={cloneActiveTab}
                onChange={(_event, next) => {
                  const nextTab = next as CloneTestsPanelTab;
                  setCloneActiveTab(nextTab);
                  if (nextTab === 'tests' && cloneDateQuickFilter === 'latest') {
                    setCloneDateQuickFilter('7d');
                  }
                }}
                aria-label="Clone tests panel tabs"
              >
                <Tab label="Tests" value="tests" />
                <Tab label="Executions" value="executions" />
              </Tabs>
            </Box>
            {cloneActiveTab === 'tests' ? (
              <Box sx={{ flex: '0 0 auto' }}>
                <TableToolbar<CloneSortKey, TestExecutionStatus>
                  title={undefined}
                  searchAriaLabel={testsTableDefinition.searchAriaLabel}
                  onDebouncedSearchChange={handleDebouncedCloneSearchChange}
                  columnVisibilityModel={cloneColumnVisibilityModel}
                  defaultColumnVisibilityModel={testsTableDefinition.defaultColumnVisibilityModel}
                  columnOptions={testsTableDefinition.columnOptions}
                  onColumnVisibilityModelChange={setCloneColumnVisibilityModel}
                  sortKey={cloneSortKey}
                  sortDirection={cloneSortDirection}
                  defaultSortKey={testsTableDefinition.defaultSortKey}
                  defaultSortDirection={testsTableDefinition.defaultSortDirection}
                  sortOptions={testsTableDefinition.sortOptions}
                  onSortChange={(nextSortKey, nextSortDirection) => {
                    setCloneSortKey(nextSortKey);
                    setCloneSortDirection(nextSortDirection);
                  }}
                  filterStatuses={cloneFilterStatuses}
                  statusOptions={testsTableDefinition.statusOptions}
                  onFilterStatusesChange={setCloneFilterStatuses}
                  filterSuites={cloneFilterSuites}
                  onFilterSuitesChange={setCloneFilterSuites}
                  suiteOptions={suiteOptions}
                  filterTags={cloneFilterTags}
                  onFilterTagsChange={setCloneFilterTags}
                  dateQuickFilter={cloneDateQuickFilter}
                  onDateQuickFilterChange={(next) => {
                    setCloneDateQuickFilter(next);
                    setCloneFromDate(null);
                    setCloneToDate(null);
                  }}
                  fromDate={cloneFromDate}
                  toDate={cloneToDate}
                  onFromDateChange={setCloneFromDate}
                  onToDateChange={setCloneToDate}
                  availableTags={cloneAvailableTags}
                  summaryCount={cloneTestsMeta?.total ?? null}
                  summarySingularLabel="test"
                  summaryPluralLabel="tests"
                />
              </Box>
            ) : null}
            {cloneActiveTab === 'executions' ? (
              <Box sx={{ flex: '0 0 auto' }}>
                <TableToolbar<CloneExecutionSortKey, TestExecutionStatus>
                  title={undefined}
                  searchAriaLabel="Search test executions"
                  onDebouncedSearchChange={handleDebouncedCloneExecutionSearchChange}
                  columnVisibilityModel={cloneExecutionColumnVisibilityModel}
                  defaultColumnVisibilityModel={cloneExecutionDefaultColumnVisibilityModel}
                  columnOptions={cloneExecutionColumnOptions}
                  onColumnVisibilityModelChange={setCloneExecutionColumnVisibilityModel}
                  sortKey={cloneExecutionSortKey}
                  sortDirection={cloneExecutionSortDirection}
                  defaultSortKey={cloneExecutionDefaultSortKey}
                  defaultSortDirection={cloneExecutionDefaultSortDirection}
                  sortOptions={cloneExecutionSortOptions}
                  onSortChange={(nextSortKey, nextSortDirection) => {
                    setCloneExecutionSortKey(nextSortKey);
                    setCloneExecutionSortDirection(nextSortDirection);
                  }}
                  filterStatuses={cloneExecutionFilterStatuses}
                  statusOptions={cloneExecutionStatusOptions}
                  onFilterStatusesChange={setCloneExecutionFilterStatuses}
                  filterSuites={cloneExecutionFilterSuites}
                  onFilterSuitesChange={setCloneExecutionFilterSuites}
                  suiteOptions={suiteOptions}
                  filterTags={cloneExecutionFilterTags}
                  onFilterTagsChange={setCloneExecutionFilterTags}
                  dateQuickFilter={cloneDateQuickFilter}
                  onDateQuickFilterChange={(next) => {
                    setCloneDateQuickFilter(next);
                    setCloneFromDate(null);
                    setCloneToDate(null);
                  }}
                  fromDate={cloneFromDate}
                  toDate={cloneToDate}
                  onFromDateChange={setCloneFromDate}
                  onToDateChange={setCloneToDate}
                  availableTags={cloneExecutionAvailableTags}
                  includeLatestQuickFilter
                  statusFilterLabel="Status"
                  summaryCount={cloneTestExecutionsMeta?.total ?? null}
                  summarySingularLabel="test execution"
                  summaryPluralLabel="test executions"
                  previewFromDate={
                    cloneDateQuickFilter === 'latest' ? cloneTestExecutionDateBounds.from : null
                  }
                  previewToDate={
                    cloneDateQuickFilter === 'latest' ? cloneTestExecutionDateBounds.to : null
                  }
                />
              </Box>
            ) : null}
          </Stack>
        </Box>
      }
      sx={{ backgroundColor: 'background.paper' }}
    >
      {cloneActiveTab === 'tests' ? (
        <TestsTable
          persistKey={null}
          queryKey={[
            'tests-table-clone',
            cloneDateQuickFilter,
            viewModel.selectedOrganisationId ?? null,
            viewModel.selectedProjectId ?? null,
            showCloneDeltas ? 'deltas-on' : 'deltas-off',
            cloneSortKey,
            cloneSortDirection,
            cloneStatusFilterKey,
            cloneSuiteFilterKey,
            cloneTagFilterKey,
            cloneDateWindow.startDay,
            cloneDateWindow.endDay,
          ]}
          queryFn={cloneQueryFn}
          pageSizeOptions={[5, 10, 25, 50]}
          initialPageSize={10}
          showDeltas={showCloneDeltas}
          sparklineRangeDays={cloneDateWindow.sparklineRangeDays}
          metricsStartDay={cloneDateWindow.startDay ?? undefined}
          metricsEndDay={cloneDateWindow.endDay ?? undefined}
          noRowsText={cloneNoRowsText}
          showToolbar={false}
          disableColumnSorting
          disableColumnMenu
          search={debouncedCloneSearch}
          onQueryMeta={setCloneTestsMeta}
          columnVisibilityModel={cloneColumnVisibilityModel}
          onColumnVisibilityModelChange={(nextModel) => {
            setCloneColumnVisibilityModel({
              ...nextModel,
              name: true,
            });
          }}
        />
      ) : null}
      {cloneActiveTab === 'executions' ? (
        <TestExecutionsTable
          persistKey={null}
          queryKey={[
            'test-executions-table-clone',
            cloneDateQuickFilter,
            viewModel.selectedOrganisationId ?? null,
            viewModel.selectedProjectId ?? null,
            cloneExecutionSortKey,
            cloneExecutionSortDirection,
            cloneExecutionStatusFilterKey,
            cloneExecutionSuiteFilterKey,
            cloneExecutionTagFilterKey,
            debouncedCloneExecutionSearch,
            cloneDateWindow.startDay,
            cloneDateWindow.endDay,
          ]}
          queryFn={cloneExecutionQueryFn}
          pageSizeOptions={[5, 10, 25, 50]}
          initialPageSize={10}
          noRowsText={cloneExecutionsNoRowsText}
          showToolbar={false}
          disableColumnSorting
          disableColumnMenu
          search={debouncedCloneExecutionSearch}
          onQueryMeta={setCloneTestExecutionsMeta}
          columnVisibilityModel={cloneExecutionColumnVisibilityModel}
          onColumnVisibilityModelChange={(nextModel) => {
            setCloneExecutionColumnVisibilityModel({
              ...nextModel,
              name: true,
            });
          }}
        />
      ) : null}
    </Panel>
  ) : undefined;

  return (
    <Stack gap="md">
      <EntityTrendsPageShell
        viewModel={viewModel}
        title="Tests"
        addButtonLabel="Add Test"
        searchAriaLabel="Search tests"
        chartTabsAriaLabel="Tests chart tabs"
        pageTabsAriaLabel="Tests page tabs"
        entityTabLabel="Tests"
        chartEmptyStateTitle="No test executions found"
        entityTableNoRowsText="No tests found"
        executionTableNoRowsText="No test executions found"
        persistKey="tests-table"
        executionPersistKey="test-executions-table"
        contentOverride={cloneContentOverride}
        renderEntityTable={({
          queryKey,
          queryFn,
          fixedFilters,
          showDeltas,
          persistKey,
          noRowsText,
        }) => (
          <TestsTable
            persistKey={persistKey}
            queryKey={queryKey}
            queryFn={queryFn}
            pageSizeOptions={[10]}
            initialPageSize={10}
            disablePageSizeSelect
            showDeltas={showDeltas}
            fixedFilters={fixedFilters}
            noRowsText={noRowsText}
          />
        )}
        renderExecutionsTable={({ fixedFilters, persistKey, noRowsText }) => (
          <TestExecutionsTable
            persistKey={persistKey}
            fixedFilters={fixedFilters}
            pageSizeOptions={[10]}
            initialPageSize={10}
            disablePageSizeSelect
            noRowsText={noRowsText}
          />
        )}
      />
    </Stack>
  );
};
